import React, { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import TextArea from '@atlaskit/textarea';
import { Box } from '@atlaskit/primitives/compiled';
import { ErrorMessage, HelperMessage } from '@atlaskit/form';

import { expValEquals } from '@confluence/feature-experiments';

export type MessageComposerProps = {
	isLockedOut: boolean;
	notifyMessage: string;
	rows: number;
	isCheckboxUnchecked: boolean;
	onNotifyMessageChange: (value: string) => void;
};

const i18n = defineMessages({
	optionalMessage: {
		id: 'share-and-restrict-dialog.cta-notify-message-footer.optional-message',
		defaultMessage: 'Optional message',
		description: 'Placeholder text for the optional message composer',
	},
	placeholderMessage: {
		id: 'share-and-restrict-dialog.cta-notify-message-footer.placeholder.non-final',
		defaultMessage: 'Add text, or use /for elements',
		description: 'Placeholder text for the optional message composer',
	},
	charLimitErrorMessage: {
		id: 'share-and-restrict-dialog.cta-notify-message-footer.char-limit-error-message',
		defaultMessage: 'Message must be less than 500 characters',
		description: 'Error message for when the message exceeds the character limit',
	},
});

export const MessageComposer: FC<MessageComposerProps> = ({
	isLockedOut,
	notifyMessage,
	rows,
	isCheckboxUnchecked,
	onNotifyMessageChange,
}) => {
	const { formatMessage } = useIntl();

	const characterLimiter = (value: string | undefined) => {
		const characterLimit = 500;
		if (value && value.length > characterLimit) {
			return <ErrorMessage>{formatMessage(i18n.charLimitErrorMessage)}</ErrorMessage>;
		} else {
			return (
				<HelperMessage>
					{value ? value.length : 0}/{characterLimit}
				</HelperMessage>
			);
		}
	};
	// TODO: Message composer optimization: https://product-fabric.atlassian.net/browse/USD-181
	return (
		<Box>
			<TextArea
				isDisabled={
					isLockedOut ||
					(isCheckboxUnchecked &&
						expValEquals('persistence_specific_access_gate', 'cohort', 'experiment'))
				}
				placeholder={formatMessage(
					expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
						? i18n.placeholderMessage
						: i18n.optionalMessage,
				)}
				value={notifyMessage}
				onChange={(e) => onNotifyMessageChange(e.target.value)}
				minimumRows={rows}
				maxHeight={
					expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
						? '120px'
						: '80px'
				}
				aria-label={formatMessage(i18n.optionalMessage)}
			/>
			{characterLimiter(notifyMessage)}
		</Box>
	);
};
